import type { Band, Clue, GuessResult, Status } from "./rules.ts";
import { MAX_GUESSES } from "./rules.ts";

export type Score = number | "x";
export interface Challenge { name: string | null; score: Score | null }

export const ARROWS = ["↑", "↗", "→", "↘", "↓", "↙", "←", "↖"];
export const SPOKEN_DIRECTIONS = ["north", "north-east", "east", "south-east", "south", "south-west", "west", "north-west"];
const EMOJI: Record<Band, string> = { correct: "🟩", nextDoor: "🟥", hot: "🟧", warm: "🟨", mild: "⬜", far: "🟦" };
const LABEL: Record<Band, string> = { correct: "Correct", nextDoor: "Next door", hot: "Hot", warm: "Warm", mild: "Mild", far: "Far" };

const tens = (km: number): string => (Math.round(km / 10) * 10).toLocaleString("en-GB");
export const distanceShort = (km: number): string => `${tens(km)} km`;
export const distanceSpoken = (km: number): string => `${tens(km)} kilometres`;
const guesses = (n: number): string => (n === 1 ? "1 guess" : `${n} guesses`);

export function guessAnnouncement(r: GuessResult, name: string): string {
  if (r.band === "correct") return `${name}, correct`;
  const parts = [name, distanceSpoken(r.distanceKm)];
  if (r.direction !== null) parts.push(SPOKEN_DIRECTIONS[r.direction]!);
  parts.push(LABEL[r.band].toLowerCase());
  return parts.join(", ");
}

export function clueAnnouncement(c: Clue): string {
  const label = { continent: "Continent", region: "Region", hint: "Hint" }[c.kind];
  return `New clue. ${label}: ${c.value}`;
}

/** "Iran is 360 km away. Head north. 4 guesses left." */
export function statusLine(last: GuessResult | undefined, name: string | undefined, left: number): string {
  const rest = left === 1 ? "1 guess left." : `${left} guesses left.`;
  if (!last || !name || last.band === "correct") return rest;
  let text = `${name} is ${distanceShort(last.distanceKm)} away.`;
  if (last.direction !== null) text += ` Head ${SPOKEN_DIRECTIONS[last.direction]}.`;
  return `${text} ${rest}`;
}

/** `site` is the game's address, e.g. "https://whereapp.world". */
export function shareText(n: number, rs: GuessResult[], status: Status, site: string): string {
  const score = status === "won" ? `${rs.length}` : "X";
  return `Where in the World? #${n} 📷 ${score}/${MAX_GUESSES}\n${rs.map((r) => EMOJI[r.band]).join("")}\n${site}/c/${n}`;
}

export const challengeUrl = (site: string, n: number, score: Score): string => `${site}/c/${n}?s=${score}`;

export function landingText(c: Challenge, isToday: boolean): { eyebrow: string; heading: string } {
  const open = "Where in the world was this taken?";
  if (c.score === null) return { eyebrow: isToday ? "TODAY'S PHOTO" : "A DAILY PHOTO", heading: open };
  const eyebrow = c.name ? `A CHALLENGE FROM ${c.name.toUpperCase()}` : "A CHALLENGE";
  if (c.score === "x") return { eyebrow, heading: c.name ? `${c.name} couldn't find this one. Can you?` : "Can you find this one?" };
  const who = c.name ? `${c.name} found this` : "Found";
  return { eyebrow, heading: `${who} in ${guesses(c.score)}. Can you beat that?` };
}

/** Only Hot or Next door counts as close, so "You were close" stays true. Same rule as the app. */
export const wasClose = (rs: GuessResult[]): boolean => rs.some((r) => r.band === "hot" || r.band === "nextDoor");

/** Above the answer: "FOUND IN 4", "YOU WERE CLOSE", "NOT THIS TIME", or "YOU 2 · SAM 3" for a challenge. */
export function summaryLine(status: Status, count: number, c: Challenge, close = false): string {
  const mine = status === "won" ? `${count}` : "X";
  if (c.score !== null) return `YOU ${mine} · ${(c.name ?? "THEM").toUpperCase()} ${c.score === "x" ? "X" : c.score}`;
  if (status === "won") return `FOUND IN ${count}`;
  return close ? "YOU WERE CLOSE" : "NOT THIS TIME";
}

/** The Get the app sheet's heading, one or two lines. */
export function sheetHeading(count: number, c: Challenge): string[] {
  const them = c.name ?? "your friend";
  if (c.score === null) return [`You found it in ${count}.`, "Can you do it again tomorrow?"];
  if (c.score === "x") return [`You found it in ${count}.`, `${c.name ?? "Your friend"} didn't.`];
  if (count < c.score) return [`You beat ${them}, ${count} to ${c.score}.`];
  if (count === c.score) return [`You matched ${them}: ${count} each.`];
  return [`${c.name ?? "Your friend"} wins this one, ${c.score} to ${count}.`, "Rematch tomorrow?"];
}
